import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

const Canvas = styled.canvas`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  pointer-events: none;
`;

/**
 * ParticleBackground - Elegant floating particles with connection lines
 * Features:
 * - GPU-accelerated canvas rendering
 * - Theme-aware colors (uses CSS variables)
 * - Performance optimizations for low-end devices
 * - Respects prefers-reduced-motion
 * - Mouse interaction (particles attracted to cursor)
 * - Responsive particle count based on screen size
 */
const ParticleBackground = ({
  enabled = true,
  particleCount = null, // Auto-calculate based on screen size
  connectionDistance = 120,
  particleSpeed = 0.5,
  particleSize = 7.95,
  lineOpacity = 0.03,
  particleOpacity = 0.12,
}) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const particlesRef = useRef([]);
  const mouseRef = useRef({ x: null, y: null, radius: 150 });
  const [shouldAnimate, setShouldAnimate] = useState(true);

  useEffect(() => {
    // Check for reduced motion preference
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      setShouldAnimate(false);
      return;
    }

    if (!enabled) {
      setShouldAnimate(false);
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d', {
      alpha: true,
      desynchronized: true, // Hint for better performance
    });

    // Set canvas size
    const setCanvasSize = () => {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = `${window.innerWidth}px`;
      canvas.style.height = `${window.innerHeight}px`;
      ctx.scale(dpr, dpr);
    };

    setCanvasSize();

    // Get theme color from CSS variables
    const getThemeColor = () => {
      const style = getComputedStyle(document.documentElement);
      const primaryColor = style.getPropertyValue('--color-primary-500').trim();
      if (primaryColor) {
        const rgb = primaryColor.split(' ');
        return `${rgb[0]}, ${rgb[1]}, ${rgb[2]}`;
      }
      return '124, 58, 237'; // Fallback to purple
    };

    // Calculate optimal particle count based on screen size and device capability
    const calculateParticleCount = () => {
      if (particleCount !== null) return particleCount;

      const area = window.innerWidth * window.innerHeight;
      const baseCount = Math.floor(area / 35000); // 1 particle per 35000px²

      // Reduce count on low-end devices
      const isLowEndDevice =
        (navigator.hardwareConcurrency && navigator.hardwareConcurrency < 4) ||
        (navigator.deviceMemory && navigator.deviceMemory < 4);

      const isMobile = window.innerWidth < 768;

      let count = baseCount;
      if (isLowEndDevice) count = Math.floor(count * 0.5);
      if (isMobile) count = Math.floor(count * 0.6);

      return Math.min(Math.max(count, 30), 150); // Between 30-150 particles
    };

    // Particle class
    class Particle {
      constructor() {
        this.reset();
        // Random initial position
        this.x = Math.random() * window.innerWidth;
        this.y = Math.random() * window.innerHeight;
      }

      reset() {
        this.x = Math.random() * window.innerWidth;
        this.y = Math.random() * window.innerHeight;
        this.vx = (Math.random() - 0.5) * particleSpeed;
        this.vy = (Math.random() - 0.5) * particleSpeed;
        this.size = Math.random() * particleSize + 1;
      }

      update() {
        // Mouse interaction - subtle attraction
        if (mouseRef.current.x !== null && mouseRef.current.y !== null) {
          const dx = mouseRef.current.x - this.x;
          const dy = mouseRef.current.y - this.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < mouseRef.current.radius) {
            const force = (mouseRef.current.radius - distance) / mouseRef.current.radius;
            const angle = Math.atan2(dy, dx);
            this.vx += Math.cos(angle) * force * 0.05;
            this.vy += Math.sin(angle) * force * 0.05;
          }
        }

        // Apply velocity
        this.x += this.vx;
        this.y += this.vy;

        // Damping
        this.vx *= 0.99;
        this.vy *= 0.99;

        // Keep within bounds with wrapping
        if (this.x < -10) this.x = window.innerWidth + 10;
        if (this.x > window.innerWidth + 10) this.x = -10;
        if (this.y < -10) this.y = window.innerHeight + 10;
        if (this.y > window.innerHeight + 10) this.y = -10;

        // Maintain minimum velocity
        const speed = Math.sqrt(this.vx * this.vx + this.vy * this.vy);
        if (speed < particleSpeed * 0.3) {
          this.vx += (Math.random() - 0.5) * 0.1;
          this.vy += (Math.random() - 0.5) * 0.1;
        }
      }

      draw(ctx, color) {
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${color}, ${particleOpacity})`;
        ctx.fill();
      }
    }

    // Initialize particles
    const initParticles = () => {
      const count = calculateParticleCount();
      particlesRef.current = [];
      for (let i = 0; i < count; i++) {
        particlesRef.current.push(new Particle());
      }
    };

    initParticles();

    // Draw connection lines between nearby particles
    const drawConnections = (ctx, color) => {
      for (let i = 0; i < particlesRef.current.length; i++) {
        for (let j = i + 1; j < particlesRef.current.length; j++) {
          const dx = particlesRef.current[i].x - particlesRef.current[j].x;
          const dy = particlesRef.current[i].y - particlesRef.current[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < connectionDistance) {
            const opacity = (1 - distance / connectionDistance) * lineOpacity;
            ctx.beginPath();
            ctx.strokeStyle = `rgba(${color}, ${opacity})`;
            ctx.lineWidth = 1;
            ctx.moveTo(particlesRef.current[i].x, particlesRef.current[i].y);
            ctx.lineTo(particlesRef.current[j].x, particlesRef.current[j].y);
            ctx.stroke();
          }
        }
      }
    };

    // Animation loop
    const animate = () => {
      if (!shouldAnimate) return;

      const color = getThemeColor();

      // Clear canvas
      ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);

      // Update and draw particles
      particlesRef.current.forEach(particle => {
        particle.update();
        particle.draw(ctx, color);
      });

      // Draw connections
      drawConnections(ctx, color);

      animationRef.current = requestAnimationFrame(animate);
    };

    // Mouse move handler
    const handleMouseMove = (e) => {
      mouseRef.current.x = e.clientX;
      mouseRef.current.y = e.clientY;
    };

    const handleMouseLeave = () => {
      mouseRef.current.x = null;
      mouseRef.current.y = null;
    };

    // Resize handler
    const handleResize = () => {
      setCanvasSize();
      initParticles();
    };

    // Start animation
    if (shouldAnimate) {
      animate();
    }

    // Event listeners
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseleave', handleMouseLeave);
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseleave', handleMouseLeave);
      window.removeEventListener('resize', handleResize);
    };
  }, [
    enabled,
    particleCount,
    connectionDistance,
    particleSpeed,
    particleSize,
    lineOpacity,
    particleOpacity,
    shouldAnimate,
  ]);

  if (!shouldAnimate || !enabled) {
    return null;
  }

  return <Canvas ref={canvasRef} aria-hidden="true" />;
};

export default ParticleBackground;
